// src/hooks/useTodayScores.ts
import { useMemo } from "react";
import { useResonanceData } from "../components/organisms/ResonanceContext/ResonanceDataContext";

const isSameLocalDay = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
};

const buildLatestScoreMaps = <T extends { sequenceId: number; doneAt: string | null; score: number }>(
  scores: T[],
  predicate?: (score: T) => boolean
) => {
  const hasScore = new Set<number>();
  const latestScores = new Map<number, number>();
  const latestScoreObjects = new Map<number, T>();

  scores.forEach((score) => {
    if (!score.doneAt) return;
    if (predicate && !predicate(score)) return;

    const seq = score.sequenceId;

    hasScore.add(seq);

    const existing = latestScoreObjects.get(seq);
    if (!existing || new Date(score.doneAt) > new Date(existing.doneAt)) {
      latestScoreObjects.set(seq, score);
      latestScores.set(seq, score.score);
    }
  });

  return {
    hasScore,
    latestScores,
    latestScoreObjects,
  };
};

/**
 * Returns:
 * - hasScoreToday: Set<sequenceId>
 * - todayScores: Map<sequenceId, lastScoreValue>
 * - todayScoreObjects: Map<sequenceId, fullScoreObject>
 */
export const useTodayScores = () => {
  const { scores } = useResonanceData();

  return useMemo(() => {
    const { hasScore, latestScores, latestScoreObjects } = buildLatestScoreMaps(
      scores ?? [],
      (score) => isSameLocalDay(score.doneAt)
    );

    return {
      hasScoreToday: hasScore,
      todayScores: latestScores,
      todayScoreObjects: latestScoreObjects,
    };
  }, [scores]);
};

/**
 * Returns the latest score available for each sequence across the full history.
 */
export const useLatestScores = () => {
  const { scores } = useResonanceData();

  return useMemo(() => {
    const { hasScore, latestScores, latestScoreObjects } = buildLatestScoreMaps(
      scores ?? []
    );

    return {
      hasLatestScore: hasScore,
      latestScores,
      latestScoreObjects,
    };
  }, [scores]);
};
